import React, { useEffect, useState } from 'react';

import { Tooltip, useMediaQuery } from '@material-ui/core';
import { Typography } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { makeStyles } from '@material-ui/core/styles';
import LockIcon from '@mui/icons-material/Lock';
import RoomIcon from '@mui/icons-material/Room';

import { exchangeRates } from 'src/const';
import { PointerPropsType } from 'src/interfaces/iproptypes';
import { IndividualListingModalV2 } from '../../Modals';
import SimplifiedAirbnbPointerBody from '../Cards/SimplifiedAirbnbPointerBody';
import {
  abbreviateNumber,
  checkAdrAnomaly,
  checkListingInactive,
  checkListingRecentlyAdded,
  checkListingUnlisted,
  convertCurrency,
  updateDynamoDBForUserVerification,
} from 'src/utils/functions';
import { Listing } from 'src/interfaces/idashboard';
import { logAmplitudeEvent } from 'src/utils/analytics';
import ListingCardErrorBoundary from 'src/App/Components/ErrorBoundaries/ListingCardErrorBoundary';

const useStylesRoot = (isEstimator: boolean = false) =>
  makeStyles({
    tooltip: {
      padding: 0,
      ...(isEstimator
        ? {
            minWidth: 280,
            width: '80vw',
            maxWidth: 500,
          }
        : {}),
    },
    pointerRevPotentialDiv: {
      position: 'relative',
      width: '80px',
      height: '30px',
      background: '#fff',
      color: '#000',
      borderRadius: 100,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      boxShadow: '0 2px 4px #0004, 0 2px 10px #0002',
      transform: 'translate(-50%, -50%)',
      transition: 'background 0.3s ease, color 0.3s ease',
      '&:hover': {
        background: '#000',
        color: '#fff',
      },
    },
    deletedPointerRevPotentialDiv: {
      position: 'relative',
      width: '80px',
      height: '30px',
      background: '#ccc',
      backgroundImage: 'repeating-linear-gradient(-45deg, #eee, #eee 2px, #ccc 2px, #ccc 4px)',
      color: '#000',
      borderRadius: 100,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      boxShadow: '0 2px 4px #0004, 0 2px 10px #0002',
      transform: 'translate(-50%, -50%)',
    },
    deprioritizedPointerRevPotentialDiv: {
      position: 'relative',
      width: '80px',
      height: '30px',
      background: '#d3d3d3',
      color: '#999',
      borderRadius: 100,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      boxShadow: '0 2px 4px #0004, 0 2px 10px #0002',
      transform: 'translate(-50%, -50%)',
      '&:hover': {
        background: '#ccc',
        color: '#333',
      },
    },
    lockIcon: {
      position: 'absolute',
      top: '-10px',
      right: '-5px',
      width: '20px',
      height: '20px',
      background: 'black',
      fontSize: '12px',
      borderRadius: 100,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
  });

const Pointer = ({
  lat,
  lng,
  data,
  listings,
  isDisabled,
  currency,
  canDelete,
  setSubscriptionModalOpen,
  handleOnSampleClick,
  subscriptionModalOpen,
  multipleBookingMarkersCallLoading,
  estimatorSampleListingData,
  showRevenuePotential,
  isEstimator,
  deletedEstimatorPointer,
  iconComponent,
  market,
  user,
  topPerformingListingOnFocus,
  hiddenListingIds,
  addHiddenListingId,
  removeHiddenListingId,
  isEstimatorLocked,
  openDemoBookingForm,
  setDemoFormHeadingFor,
  isHistoricalDataHidden,
  onEstimatorLockedMarkerClick,
  onEstimatorLockedMarkerHistoricalDataClick,
  referenceADR,
  cachedMarketData,
  dataKey,
}: PointerPropsType) => {
  const matches = useMediaQuery('(max-width:550px)');

  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);

  const [isHidden, setIsHidden] = useState<boolean>(false);
  const isListingRecentlyAdded = checkListingRecentlyAdded(data as Listing);
  const isListingInactive = checkListingInactive(data as Listing);
  const isListingUnlisted = checkListingUnlisted(data as Listing);

  useEffect(() => {
    let hidden = false;
    if (hiddenListingIds) {
      hiddenListingIds.forEach(id => {
        if (id === data['listingID']) {
          hidden = true;
        }
      });
    }
    setIsHidden(hidden);
  }, [hiddenListingIds]);

  const isCurrentMarkerOnFocus =
    topPerformingListingOnFocus && data?.['listingID'] === topPerformingListingOnFocus;

  const getPointerSize = (adr: any = undefined) => {
    const defaultSize: number = 14;
    if (!adr || isNaN(adr)) return defaultSize;
    if (adr < 100) {
      return defaultSize;
    } else if (adr < 250) {
      return defaultSize * 1.3;
    } else {
      return defaultSize * 1.5;
    }
  };

  let averageADR = 0;
  let adrCnt = 0;

  averageADR = data['adrLTM'];

  const isPointerFromEstimatorAndDisabled = isEstimator && isDisabled;
  const isAdrAnomaly = checkAdrAnomaly((data as Listing).adrLTM, referenceADR || null);

  const rootClass = useStylesRoot(isEstimator)();
  let icon: JSX.Element = isCurrentMarkerOnFocus ? (
    <RoomIcon
      style={{
        position: 'relative',
        fontSize: getPointerSize(averageADR),
        stroke: '#fff',
        color: 'red',
        transformOrigin: 'center',
        transform: `scale(3) translateY(-50%)`,
        zIndex: '+9999999',
        borderRadius: 900,
        animation: 'outline-pulse 1.3s ease infinite',
      }}
      onMouseLeave={() => {
        setTooltipOpen(false);
      }}
      onClick={() => {
        setTooltipOpen(true);
      }}
    />
  ) : (
    <div
      style={{
        position: 'relative',
        width: '17px',
        height: '10px',
        background: '#fff',
        borderRadius: 50,
        boxShadow: '0 2px 4px #0004, 0 2px 10px #0002',
      }}
      onMouseLeave={() => {
        setTooltipOpen(false);
      }}
      onClick={() => {
        setOpenFullView(true);
      }}
    ></div>
  );

  if (showRevenuePotential) {
    const currencySymbol = exchangeRates[currency].symbol;
    const adr = data['adrLTM'];
    const occupancyRate = data['occupancyRateLTM'];
    const revPar = (adr * occupancyRate) / 100;

    const formattedRevPar = `${currencySymbol}${abbreviateNumber(
      convertCurrency(currency, revPar)
    )}`;
    const formattedAdr = `${currencySymbol}${abbreviateNumber(convertCurrency(currency, adr))}`;
    const formattedOccupancyRate = `${occupancyRate}%`;

    const performanceMetrics = {
      adr: formattedAdr,
      occupancyRate: formattedOccupancyRate,
      revPar: formattedRevPar,
    };

    icon = (
      <div
        translate="no"
        className={
          isHidden
            ? rootClass.deletedPointerRevPotentialDiv
            : isListingInactive || isListingRecentlyAdded || isListingUnlisted || isAdrAnomaly
            ? rootClass.deprioritizedPointerRevPotentialDiv
            : rootClass.pointerRevPotentialDiv
        }
        onClick={() => {
          onEstimatorLockedMarkerClick && onEstimatorLockedMarkerClick();
          setOpenFullView(true);
        }}
        onMouseLeave={() => {
          !matches && setTooltipOpen(false);
        }}
      >
        <Typography
          translate="no"
          variant="body1"
          className="notranslate"
          sx={{ fontWeight: 900, color: deletedEstimatorPointer ? '#aaa' : 'inherit' }}
        >
          {isPointerFromEstimatorAndDisabled ? (
            <LockIcon fontSize="inherit" />
          ) : isListingRecentlyAdded ? (
            'New'
          ) : isListingInactive ? (
            'Inactive'
          ) : isListingUnlisted ? (
            'Unlisted'
          ) : isAdrAnomaly ? (
            'Unavailable'
          ) : !isEstimator && isDisabled ? (
            formattedAdr
          ) : dataKey ? (
            performanceMetrics[dataKey]
          ) : (
            formattedRevPar
          )}
        </Typography>
        {((isEstimator && !data['is_premium']) || isDisabled) && (
          <div className={rootClass.lockIcon}>
            <LockIcon htmlColor="#fff" fontSize="inherit" />
          </div>
        )}
      </div>
    );
  }

  if (iconComponent) {
    icon = iconComponent;
  }

  const [openFullView, setOpenFullView] = useState<boolean>(false);

  return (
    <>
      {!openFullView && (
        <ClickAwayListener
          onClickAway={(event: MouseEvent | TouchEvent) => {
            // Only close if the tooltip is open and the click/touch is outside
            const target: any = event.target as HTMLElement;
            const isTooltip = target.closest && target.closest('.MuiTooltip-tooltip');

            if (tooltipOpen && !isTooltip) {
              setTooltipOpen(false);
            }
          }}
        >
          <Tooltip
            interactive
            disableTouchListener
            disableFocusListener={matches ? true : false}
            classes={{
              tooltip: rootClass.tooltip,
            }}
            // open={tooltipOpen && !isPointerFromEstimatorAndDisabled}
            open={tooltipOpen}
            onOpen={() => {
              if (!tooltipOpen) {
                logAmplitudeEvent('listing_markers_hovered');
              }
              setTooltipOpen(true);
              if (market) updateDynamoDBForUserVerification(market, setSubscriptionModalOpen, user);
            }}
            onClose={() => {
              !matches && setTooltipOpen(false);
            }}
            style={{
              zIndex: 999999,
            }}
            enterTouchDelay={0}
            leaveTouchDelay={5000}
            title={
              <ListingCardErrorBoundary>
                <SimplifiedAirbnbPointerBody
                  data={data as any[]}
                  setOpenFullView={setOpenFullView}
                  currency={currency}
                  canDelete={deletedEstimatorPointer ? true : canDelete}
                  isDisabled={deletedEstimatorPointer ? true : isDisabled}
                  deleteListing={() => {}}
                  estimatorSampleListingData={estimatorSampleListingData}
                  isEstimator={isEstimator ? true : false}
                  isHidden={isHidden}
                  handleOnSampleClick={handleOnSampleClick}
                  hiddenListingIds={hiddenListingIds}
                  addHiddenListingId={addHiddenListingId}
                  removeHiddenListingId={removeHiddenListingId}
                  isEstimatorLocked={isEstimatorLocked}
                  referenceADR={referenceADR}
                />
              </ListingCardErrorBoundary>
            }
          >
            {icon}
          </Tooltip>
        </ClickAwayListener>
      )}

      {openFullView && (
        <IndividualListingModalV2
          open={openFullView}
          onClose={() => setOpenFullView(false)}
          data={data as any[]}
          currency={currency}
          isDisabled={isDisabled}
          listings={listings}
          setSubscriptionModalOpen={setSubscriptionModalOpen}
          handleOnSampleClick={handleOnSampleClick}
          subscriptionModalOpen={subscriptionModalOpen}
          multipleBookingMarkersCallLoading={multipleBookingMarkersCallLoading}
          estimatorSampleListingData={estimatorSampleListingData}
          isEstimator={isEstimator}
          openDemoBookingForm={openDemoBookingForm ? openDemoBookingForm : () => {}}
          setDemoFormHeadingFor={setDemoFormHeadingFor}
          isHistoricalDataHidden={isHistoricalDataHidden}
          onEstimatorLockedMarkerHistoricalDataClick={onEstimatorLockedMarkerHistoricalDataClick}
          referenceADR={referenceADR}
          cachedMarketData={cachedMarketData}
        />
      )}
    </>
  );
};

export default React.memo(Pointer);
